import React, { useState } from 'react';
import { LogOut, User } from 'lucide-react';
import { Button } from './ui/button';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { useAuth } from '@/contexts/AuthContext';

interface UserProfileProps {
  className?: string;
}

const UserProfile: React.FC<UserProfileProps> = ({ className }) => {
  const { user, loading, signIn, signOut } = useAuth();
  const [signingIn, setSigningIn] = useState(false);
  const [imageError, setImageError] = useState(false);

  const handleSignIn = () => {
    try {
      setSigningIn(true);
      // Redirect to sign-in page
      window.location.href = '/sign-in';
    } catch (error) {
      console.error('Sign-in error:', error);
      alert('Failed to initiate sign-in. Please try again.');
    } finally {
      setSigningIn(false);
    }
  };

  if (loading) {
    return (
      <Button 
        variant="outline" 
        size="sm" 
        onClick={handleSignIn}
        disabled={loading}
        className={className}
      >
        {loading ? "Loading..." : "Sign In"}
      </Button>
    );
  }

  if (!user) {
    return (
      <Button 
        variant="outline" 
        size="sm" 
        onClick={handleSignIn}
        disabled={signingIn}
        className={className}
      >
        {signingIn ? "Connecting..." : "Sign In"}
      </Button>
    );
  }

  // Extract initials for the avatar
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  const initials = getInitials(user.fullName || 'User');
  
  // Function to handle image URL preparation
  const getImageUrl = (url: string) => {
    if (!url) return '';
    
    // Use the proxy endpoint to avoid CSP issues in Cloudflare
    return `/api/proxy-image?url=${encodeURIComponent(url)}`;
  };

  // Enhanced dropdown menu for user profile
  return (
    <div className={`flex items-center ${className}`}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="relative p-0 border-0" role="button" aria-haspopup="menu">
            <Avatar className="h-8 w-8 bg-blue-600 text-white cursor-pointer">
              {user.profileImageUrl && !imageError ? (
                <AvatarImage 
                  src={getImageUrl(user.profileImageUrl)} 
                  alt="User profile"
                  onError={() => setImageError(true)}
                  crossOrigin="anonymous"
                />
              ) : (
                <AvatarFallback className="bg-blue-600 text-white text-sm font-medium">
                  {initials}
                </AvatarFallback>
              )}
            </Avatar>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48 p-2">
          <div className="py-2 px-3 border-b border-gray-100 mb-2">
            <p className="font-medium text-sm truncate">{user.fullName}</p>
            <p className="text-xs text-muted-foreground truncate">{user.emailAddresses[0]?.emailAddress}</p>
          </div>
          
          <DropdownMenuItem 
            onClick={signOut} 
            className="text-red-500 hover:text-red-600 hover:bg-red-50 cursor-pointer"
          >
            <LogOut size={16} className="mr-2" />
            <span>Logout</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default UserProfile;